import { createFileRoute, Link } from "@tanstack/react-router";
import { useState, useEffect } from "react";
import { ArrowDownCircle, MessageCircle, Leaf, Sprout } from "lucide-react";
import {
  getCurrentEventLabel,
  getMillisecondsUntilNextEventDateChange,
} from "@/lib/whatsapp-groups";


export const Route = createFileRoute("/obrigado")({
  head: () => ({
    meta: [
      { title: "Falta um passo — Cura pela Alimentação" },
      {
        name: "description",
        content:
          "Entre no grupo de WhatsApp da masterclass Cura pela Alimentação para receber o link da aula ao vivo e os avisos.",
      },
      { property: "og:title", content: "Falta um passo — Cura pela Alimentação" },
      {
        property: "og:description",
        content:
          "Entre no grupo de WhatsApp da masterclass para garantir o link da aula ao vivo.",
      },
    ],
  }),
  component: Obrigado,
});

function Obrigado() {
  const [eventLabel, setEventLabel] = useState(() => getCurrentEventLabel());

  useEffect(() => {
    setEventLabel(getCurrentEventLabel());
    const t = window.setTimeout(() => {
      setEventLabel(getCurrentEventLabel());
    }, getMillisecondsUntilNextEventDateChange() + 100);
    return () => window.clearTimeout(t);
  }, [eventLabel]);

  return (
    <main className="relative flex min-h-screen items-start justify-center overflow-hidden px-4 pt-3 pb-6 sm:items-center sm:py-10">
      {/* ambient glow */}
      <div aria-hidden className="pointer-events-none absolute inset-0">
        <div className="absolute -top-32 left-1/2 h-[480px] w-[480px] -translate-x-1/2 rounded-full bg-leaf/20 blur-3xl" />
        <div className="absolute bottom-0 right-0 h-[320px] w-[320px] rounded-full bg-gold/10 blur-3xl" />
      </div>

      <article
        className="relative z-10 w-full max-w-[440px] overflow-hidden rounded-3xl bg-card px-6 py-10 text-center sm:px-8 sm:py-12"
        style={{ boxShadow: "var(--shadow-card)" }}
      >
        {/* Floating decorative leaves */}
        <Leaf
          className="absolute left-5 top-5 size-6 text-leaf-bright/60 float-soft"
          strokeWidth={1.5}
        />
        <Sprout
          className="absolute right-5 top-7 size-6 text-lime/60 float-soft"
          style={{ animationDelay: "1.2s" }}
          strokeWidth={1.5}
        />

        {/* Success icon */}
        <div className="mx-auto flex size-20 items-center justify-center rounded-full border border-gold/40 bg-gold/10 backdrop-blur-sm">
          <ArrowDownCircle className="size-10 text-gold" strokeWidth={2.2} />
        </div>

        {/* Tag */}
        <p className="mt-6 text-[11px] font-semibold uppercase tracking-[0.3em] text-gold">
          Inscrição ainda pendente
        </p>

        {/* Headline */}
        <h1 className="mt-3 font-display text-4xl font-bold uppercase leading-[1.05] text-cream sm:text-5xl">
          Falta um passo
        </h1>

        {/* Subheadline */}
        <p className="mx-auto mt-5 max-w-[340px] text-[15px] font-semibold leading-relaxed text-leaf-bright">
          Entre agora no grupo de WhatsApp para receber o link da aula ao vivo
          e os avisos exclusivos 👇
        </p>

        {/* CTA WhatsApp */}
        <Link
          to="/obrigado-grupo"
          className="cta-pulse group relative mt-8 inline-flex w-full items-center justify-center gap-3 rounded-2xl px-5 py-5 text-base font-extrabold uppercase tracking-wider text-white transition-all duration-300 hover:scale-[1.03] hover:brightness-110 active:scale-[0.99]"
          style={{
            background: "linear-gradient(135deg, #25D366 0%, #128C7E 100%)",
          }}
        >
          <MessageCircle className="size-6" strokeWidth={2.5} />
          Entrar no grupo agora
        </Link>

        <p className="mt-3 text-[12px] font-semibold uppercase tracking-wider text-gold">
          ⚠️ Clique acima para receber o link da aula
        </p>

        {/* Live badge */}
        <div className="mt-6 flex justify-center">
          <span className="inline-flex items-center gap-2 rounded-full border border-leaf/40 bg-leaf/10 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-cream">
            <span className="size-2 rounded-full bg-leaf-bright live-dot" />
            Ao vivo • {eventLabel}
          </span>
        </div>

        <p className="mt-5 text-[11px] leading-relaxed text-muted-foreground">
          Sem o grupo, você pode perder o link da transmissão. A aula é única,
          sem replay.
        </p>
      </article>
    </main>
  );
}
